import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from 'react-query';
import { Form, Input, InputNumber, notification, Popconfirm } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { indexBy } from 'utils/utils2';
import { PlusOutlined } from '@ant-design/icons';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import DeleteButton from 'components/DeleteButton';
import { useFormList } from 'hooks/useFormList';
import DF from 'DisplayFramework';
import ButtonOperation from 'components/ButtonOperation';
import SaveButton from 'components/SaveButtonAction';
import {
  addParAssetFvContextDegradation,
  deleteParAssetFvContextDegradation,
  getParassetfvcontextdegradationByParcontextid,
  ParAssetFvContextDegradationResponse,
  ParassetfvcontextuseRequest,
  updateParAssetFvContextDegradation,
} from 'services/assetconfig';
import ParListSelect from 'components/ParListSelect';
import { isNil } from 'lodash';
import InputRate from 'components/InputRate';
import { AuthContext } from 'components/Auth/Auth';
import { setValueByNamePath } from 'utils/utils2';

const NEW_ACTION_ID = -1;
export type ParAssetFVContextDegradationListProps = {
  parcontextid: number;
  refetch: () => void;
};
const ParAssetFVContextDegradationList = (
  props: ParAssetFVContextDegradationListProps
) => {
  const [form] = useForm();
  const fieldName = 'ParAssetFVcontextDegradation';
  const { add } = useFormList(form, fieldName, {
    idKey: 'fvuseid',
    addAsFirst: true,
  });
  const { isInspect } = useContext(AuthContext);
  const { data: asset, refetch } = useQuery(
    [
      'getParassetfvcontextdegradationByParcontextid',
      { parcontextid: props.parcontextid },
    ],
    () =>
      getParassetfvcontextdegradationByParcontextid({
        parcontextid: Number(props.parcontextid),
      }).then((res) => {
        return res.list || [];
      }),
    {
      enabled: !isNil(props.parcontextid) && !isNaN(Number(props.parcontextid)),
    }
  );
  const [method, setMethod] = useState('');
  useEffect(() => {
    if (asset) {
      form.setFieldsValue({
        [fieldName]: asset,
      });
    }
  }, [form, asset]);
  const getName = useCallback(
    (name: string) => (record: ParAssetFvContextDegradationResponse) => {
      const actions = form.getFieldValue(fieldName);
      const index = indexBy(actions, 'fvuseid')[record.fvuseid];
      return [fieldName, index, name];
    },
    [form]
  );
  const onSave = useCallback(
    (record: ParAssetFvContextDegradationResponse) => {
      const condition: ParAssetFvContextDegradationResponse[] =
        form.getFieldValue(fieldName);
      const index = indexBy(condition, 'fvuseid')[record.fvuseid];
      const currentValue = {
        ...condition[index],
      };
      const { fvuseid } = currentValue;

      if (fvuseid === NEW_ACTION_ID) {
        addParAssetFvContextDegradation({
          request: { ...currentValue, fvuseid: null as any },
        }).then((response) => {
          if (response.code === '200') {
            notification.success({ message: response.message });
          }
          refetch();
        });
      } else {
        return updateParAssetFvContextDegradation({
          fvuseid: Number(fvuseid),
          request: currentValue,
        }).then((response) => {
          if (response.code === '200') {
            notification.success({
              message: response.message,
            });
            refetch();
          } else {
            notification.error({
              message: response.message,
            });
          }
        });
      }
    },
    [form, refetch]
  );

  const columns = useMemo<
    DataGridColDef<ParAssetFvContextDegradationResponse>[]
  >(
    () => [
      {
        dataIndex: 'fvuseid',
        render: (value, record, index) => {
          return record?.fvuseid === NEW_ACTION_ID ? (
            <Form.Item
              noStyle
              name={getName('fvuseid')(record)}
              rules={[{ required: true }]}
            >
              <Input disabled />
            </Form.Item>
          ) : (
            <Form.Item>{value}</Form.Item>
          );
        },
        width: '60px',
        fixed: true,
      },
      {
        dataIndex: 'attcode',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('attcode')(record)}
            rules={[{ required: true }]}
          >
            <ParListSelect parcode="ASSETUSE" />
          </Form.Item>
        ),
        width: '110px',
      },

      {
        dataIndex: 'usevaluemin',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('usevaluemin')(record)}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: '100px',
      },
      {
        dataIndex: 'usevaluemax',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('usevaluemax')(record)}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: '100px',
      },
      {
        dataIndex: 'usedegradationmethod',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('usedegradationmethod')(record)}
            rules={[{ required: true }]}
          >
            <ParListSelect
              parcode="USEDEGMETH"
              onSelect={(e) => {
                setMethod(e);
                setValueByNamePath(
                  form,
                  getName('degradationcoef')(record),
                  null
                );
                setValueByNamePath(form, getName('degradationfixedamount')(record), null);
              }}
            />
          </Form.Item>
        ),
        width: '110px',
      },
      {
        dataIndex: 'degradationcoef',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('degradationcoef')(record)}
            rules={[{ required: true }]}
          >
            <InputRate
              style={{ width: '100%' }}
              disabled={method === 'AMOUNT'}
            />
          </Form.Item>
        ),
        width: '110px',
      },
      {
        dataIndex: 'degradationfixedamount',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('degradationfixedamount')(record)}
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: '100%' }} disabled={method === 'COEF'} />
          </Form.Item>
        ),
        width: '110px',
      },

      {
        dataIndex: 'Actions',
        render: (_, record, index) =>
          record?.fvuseid === NEW_ACTION_ID ? (
            <SaveButton
              comcode="save"
              size="small"
              onClick={() => !isInspect && onSave(record)}
              htmlType="button"
            />
          ) : (
            <>
              <SaveButton
                comcode="update"
                size="small"
                onClick={() => !isInspect && onSave(record)}
                htmlType="button"
              />

              <Popconfirm
                title="Are you sure to delete this Condition?"
                onConfirm={() => {
                  deleteParAssetFvContextDegradation({
                    fvuseid: record.fvuseid,
                  }).then((res) => {
                    notification.success({ message: res.message });
                    refetch();
                  });
                }}
              >
                <DeleteButton onlyIcon />
              </Popconfirm>
            </>
          ),
        width: '110px',
        fixed: 'left',
      },
    ],

    [getName, isInspect, method, onSave, refetch]
  );
  const addRow = () => {
    const row: ParassetfvcontextuseRequest = {
      fvuseid: NEW_ACTION_ID,
      attcode: '',
      parcontextid: props.parcontextid,
      usevaluemax: 0,
      usevaluemin: 0,
    };
    add(row);
  };
  return (
    <DF.Provider pagcode="parassetfvcontextdetail">
      <Form form={form}>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const branchs: ParAssetFvContextDegradationResponse[] =
              getFieldValue(fieldName);
            const isNewItemInTable =
              asset?.find(({ fvuseid }) => fvuseid === NEW_ACTION_ID) !==
              undefined;
            return (
              <DataGrid
                titleComcode="ParAssetFVContextDegradationList"
                rowKey="fvuseid"
                columns={columns}
                dataSource={branchs}
                hideFilters
                extraActions={
                  <ButtonOperation
                    comcode="Add"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => !isInspect && addRow()}
                    disabled={isNewItemInTable}
                  />
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};

export default ParAssetFVContextDegradationList;
